/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useHistory, useLocation, useParams } from 'react-router-dom';
import { Typography, Paper, Box, Stack, Grid, Link, Breadcrumbs, Tooltip, CircularProgress } from '@mui/material';
import { FieldArray, Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';
import { ROUTES } from '../../../../constants/routes';
import { CustomInput } from '../../../../components/CustomInput';
import { CustomInputWithMask } from '../../../../components/CustomInputWithMask';
import { CustomButton } from '../../../../components/CustomButton';
import { ImageUploader } from '../../../../components/ImageUploader';
import { REGULAR_EXPRESSIONS } from '../../../bonuses/constants';
import { CustomModal } from '../../../../components/CustomModal';
import { getCities, getPartnerById, postCreatePartner, updatePartner } from '../../../../store/partners/actions';
import { CustomMultiSelect } from '../../../../components/CustomMultiSelect';
import AddIcon from '@mui/icons-material/Add';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { requestUploadLogo } from '../../../../api/partners';
import { setUsersPermissions } from '../../../../utils/setUsersPermissions';
import { visibleBySectionPermission } from '../../../../utils/visibleBySectionPermission';
import { PERMISSIONS } from '../../../../constants/variables';

const validationSchema = yup.object({
  name: yup
    .string()
    .required('Обязательное поле'),
  bin_number: yup
    .string()
    .min(12, 'Недопустимый формат ввода данных. Поле должно содержать 12 цифр')
    .max(12, 'Недопустимый формат ввода данных. Поле должно содержать 12 цифр')
    .test('unique symbols', 'Недопустимый формат ввода данных', val => {
      if (val !== undefined) {
        for (let i = 1; i < val.length; i++) if (val.charAt(i) !== val.charAt(i - 1)) return true;
        return false;
      } else return true;
    })
    .required('Обязательное поле'),
  phones: yup.array().of(
    yup.string().test('len', 'Must be exactly 15 characters', val => {
      return val?.length === 15 || val === undefined;
    })
  ),
  contact_person: yup.string().min(2, 'Минимум 2 символа').max(256, 'Максимум 256 символов').nullable(),
  email: yup.string().test(
    'email',
    'Некорректный email',
    val => val ?
      REGULAR_EXPRESSIONS.email_validation_pattern.test(val)
      : true
  ).required('Обязательное поле'),
});

export const AddPartner = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();
  const isEdit = id !== ':id';

  const [isCancelModal, setIsCancelModal] = useState(false);
  const [customTouched, setCustomTouched] = useState({});
  const [updatesFieldsStatus, setUpdatesFieldsStatus] = useState(false);

  const { userPermissions, currentUsersPermissions } = useSelector((state) => state.userPermissions);
  const permissions = setUsersPermissions(userPermissions, currentUsersPermissions?.permissions);
  const { partnerCard, fetching, cities: branches, error } = useSelector(state => state.partners);
  const [logo, setLogo] = useState(partnerCard?.logo ? `/partners/${partnerCard?.logo}` : '');

  const cities = branches
    ?.map((city, index, array) => array.findIndex(item => city.name === item.name) === index && city)
    .filter(city => city)
    .map(city => ({ label: city?.name, value: city?.id }));
  cities?.unshift({ label: 'Все', value: 'all' });

  const citiesForInitial = partnerCard?.cities?.map(city => ({ label: city?.name, value: city?.id })) || [];
  const initialCities = citiesForInitial.length === cities?.slice(1)?.length ? [{ label: 'Все', value: 'all' }, ...citiesForInitial] : citiesForInitial;

  const initialValues = {
    name: isEdit ? partnerCard.name : null,
    bin_number: isEdit ? partnerCard.bin_number : null,
    contact_person: isEdit ? partnerCard.contact_person :  '',
    email: isEdit ? partnerCard.email : null,
    phones: isEdit ? partnerCard.phones?.length ? partnerCard.phones?.map(phone => phone.substr(1)) : [''] : [''],
    city_ids: isEdit ? initialCities : [],
    logo: isEdit ? partnerCard?.logo ? `/partners/${partnerCard?.logo}` : '' : ''
  };

  const touchedById = (name) => isEdit && setCustomTouched({ [name]: true, ...customTouched });

  const errorTrigger = (values, errors, name) =>
    (typeof values[name] === 'string' || typeof values[name] === 'number') &&
    JSON.stringify(initialValues) !== JSON.stringify({ ...values, phones: values.phones.map(phone => phone.split(' ').join('')) }) &&
    errors[name];

  const headerTitle = isEdit ?
    'Редактировать партнёра' :
    'Добавить партнёра';

  const isCreatingFromMakingPromotion = !!location.state?.from?.match(ROUTES.PARTNERS_ADD_PROMOTION);

  useEffect(() => {
    isEdit && dispatch(getPartnerById(Number(id)));
    !branches.length && dispatch(getCities());
  }, []);

  const updateFieldData = (fetch) => {
    dispatch(fetch({ update: true })).then(res => {
      if (res.status === 200) {
        setUpdatesFieldsStatus(true);
      }
    });
  };

  const uploadLogo = async (file) => {
    let formData = new FormData();
    formData.append('uploaded_file', file);
    const { data } = await requestUploadLogo(formData);
    setLogo(data.relative_path);
    touchedById('logo');
  };

  if (!isEdit && !visibleBySectionPermission(permissions, [PERMISSIONS.PARTNERS.createPartners.value])) return null;
  if (isEdit && !visibleBySectionPermission(permissions, [PERMISSIONS.PARTNERS.updatePartners.value])) return null;

  if (fetching) {
    return (
      <Box
        sx={{
          marginTop: '150px',
          textAlign: 'center'
        }}>
        <CircularProgress data-testid="CircularProgress" />
      </Box>
    );
  }

  return (
    <>
      <Typography mb="24px" variant="h1" sx={{ color: 'text.primary' }} component="h1">
        {headerTitle}
      </Typography>
      <Breadcrumbs>
        <Link component={RouterLink} underline="hover" color="inherit" to={ROUTES.PARTNERS}>
          Партнёры
        </Link>
        <Box>{headerTitle}</Box>
      </Breadcrumbs>

      <Paper
        sx={{
          padding: '24px',
          marginTop: '24px'
        }}
      >
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          validateOnChange
          validateOnBlur
          enableReinitialize
          render={({
             values,
             handleChange,
             setFieldValue,
             errors,
             dirty,
             isValid
          }) => (
            <Form>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  marginBottom: '24px'
                }}>
                <Box sx={{ display: 'flex', marginBottom: '24px' }}>
                  <Box
                    sx={{
                      marginRight: '24px'
                    }}>
                    <ImageUploader
                      name="logo"
                      avatar={values.logo}
                      setFieldValueWithoutForm={uploadLogo}
                      setFieldValue={setFieldValue}
                      isCompany
                    />
                  </Box>

                  <Stack spacing={2}>
                    <CustomInput
                      placeholder="Наименование партнёра"
                      onChange={(e) => {
                        touchedById('name');
                        return handleChange(e);
                      }}
                      variant="outlined"
                      sx={{ width: 344, mt: 3 }}
                      size="small"
                      name="name"
                      autoComplete="off"
                      value={values['name']}
                      error={errorTrigger(values, errors, 'name')}
                      helperText={errorTrigger(values, errors, 'name')}
                    />
                    <CustomInput
                      placeholder="БИН"
                      onChange={(e) => {
                        touchedById('bin_number');
                        return handleChange(e);
                      }}
                      variant="outlined"
                      sx={{ width: 344 }}
                      size="small"
                      name="bin_number"
                      autoComplete="off"
                      value={values['bin_number']}
                      error={errorTrigger(values, errors, 'bin_number')}
                      helperText={errorTrigger(values, errors, 'bin_number')}
                    />
                  </Stack>
                </Box>
                <Grid item mb="24px">
                  <CustomInput
                    placeholder="Email"
                    autoComplete="off"
                    onChange={(e) => {
                      touchedById('email');
                      return handleChange(e);
                    }}
                    variant="outlined"
                    sx={{ width: 496 }}
                    size="small"
                    name="email"
                    value={values['email']}
                    error={errorTrigger(values, errors, 'email')}
                    helperText={errorTrigger(values, errors, 'email')}
                  />
                </Grid>
                <Grid container spacing={2} sm={6} mb="24px">
                  <FieldArray
                    name="phones"
                    validateOnChange={false}
                    render={(arrayHelpers) => (
                      <>
                        {values?.phones?.map((item, index) => (
                          <Grid item key={index}>
                            <Box sx={{ display: 'flex', alignItems: 'center', maxWidth: 240 }}>
                              <CustomInputWithMask
                                name={`phones.${index}`}
                                value={item}
                                placeholder="Номер телефона"
                                onChange={e => {
                                  touchedById('phones');
                                  return handleChange(e);
                                }}
                                onKeyDown={(event) => {if (!item && event.key === 'Backspace' && values.phones.length !== 1) arrayHelpers.remove(index)}}
                                variant="outlined"
                                sx={{ width: 240 }}
                                size="small"
                              />
                              {values.phones.length === index + 1 && index < 4 && (
                                <AddIcon
                                  sx={{ color: 'primary.main', cursor: 'pointer' }}
                                  onClick={() => arrayHelpers.push('')}
                                  data-testid="AddPhone"
                                />
                              )}
                            </Box>
                          </Grid>
                        ))}
                      </>
                    )}
                  />
                </Grid>
                <Grid container mb="24px" alignItems="center" justifyContent="space-between" width="540px">
                  <Grid container gap="16px" sx={{ width: '448px' }}>
                    <CustomMultiSelect
                      name="city_ids"
                      value={values['city_ids']}
                      error={errorTrigger(values, errors, 'city_ids')}
                      helperText={errorTrigger(values, errors, 'city_ids')}
                      onChange={handleChange}
                      variant="outlined"
                      options={cities}
                      width={498}
                      placeholderColor="#667A8B"
                      setFieldValue={setFieldValue}
                      size="small"
                      placeholder="Города"
                      touchedBy={() => touchedById('city_ids')}
                      showAll={true}
                    />
                  </Grid>
                  <Tooltip title="Обновить данные" placement="top">
                    <RestartAltIcon
                      onClick={() => updateFieldData(getCities)}
                      sx={{
                        color: updatesFieldsStatus ? 'success.main' : 'text.primary',
                        cursor: 'pointer'
                      }}
                      data-testid="RestartAltIcon"
                    />
                  </Tooltip>
                </Grid>
                <Grid item>
                  <CustomInput
                    autoComplete="off"
                    placeholder="Контактное лицо"
                    onChange={(e) => {
                      touchedById('contact_person');
                      return handleChange(e);
                    }}
                    variant="outlined"
                    sx={{ width: 496, zIndex: '0' }}
                    size="small"
                    setFieldValue={setFieldValue}
                    name="contact_person"
                    value={values['contact_person']}
                    error={errorTrigger(values, errors, 'contact_person')}
                    helperText={errorTrigger(values, errors, 'contact_person')}
                  />
                </Grid>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography color="error.main" width="500px">
                  {error?.detail && (typeof error?.detail === 'string' ? error?.detail : JSON.stringify(error?.detail)) || (typeof error === 'string' && error)}
                </Typography>
                <Box>
                  <CustomButton
                    color="secondary"
                    variant="contained"
                    sx={{ marginRight: '16px' }}
                    onClick={() => {
                      if (JSON.stringify(initialValues) === JSON.stringify(values))
                        history.push(isCreatingFromMakingPromotion ? ROUTES.PARTNERS_ADD_PROMOTION : ROUTES.PARTNERS);
                      setIsCancelModal(true);
                    }}
                  >
                    Отмена
                  </CustomButton>
                  <CustomButton
                    type="submit"
                    variant="contained"
                    disabled={isEdit ? !Object.values(customTouched).length || !isValid || !dirty : !isValid || !dirty}
                  >
                    Сохранить
                  </CustomButton>
                </Box>
              </Box>
            </Form>
          )}
          onSubmit={values => {
              const transformedValues = {
                ...values,
                phones: values.phones.map(phone => phone ? `7${phone}`.split(' ').join('') : null).filter(phone => phone),
                city_ids: values.city_ids.map(city => city.value).filter(id => id !== 'all'),
                logo: logo || undefined
              }
              if (isEdit)
                dispatch(updatePartner(id, Object.keys(transformedValues).reduce((acc, item) => {
                  customTouched[item] ? (acc[item] = transformedValues[item]) : {};
                  return acc;
                }, {}), () => isCreatingFromMakingPromotion ? history.push(ROUTES.PARTNERS_ADD_PROMOTION) : history.push(ROUTES.PARTNERS)))
              else
                dispatch(postCreatePartner(transformedValues, () => isCreatingFromMakingPromotion ? history.push(ROUTES.PARTNERS_ADD_PROMOTION) : history.push(ROUTES.PARTNERS)));
          }}
        />
        <CustomModal
          opened={isCancelModal}
          modalClose={() => setIsCancelModal(false)}
          modalSubmit={() => history.push(isCreatingFromMakingPromotion ? ROUTES.PARTNERS_ADD_PROMOTION : ROUTES.PARTNERS)}
          text="Вы уверены, что хотите выйти, не сохранив данные?"
          cancelBtnName="Отмена"
          submitBtnName="Да"
        />
      </Paper>
    </>
  );
};
